import styled, { keyframes } from 'styled-components';
import ReactDOM from 'react-dom';
import React, {
  createContext,
  DependencyList,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Styling } from '@/styling';
import { Backdrop } from '@/components/visual/Backdrop';
import { Box } from '@/components/layout/Box';

export type DialogProps2 = {
  onClose?(): void;
};

type DialogComponent = FC<DialogProps2>;

export type DialogOptions2 = {
  /**
   * Determines whether the dialog window should be closed when the user clicks
   * outside of it.
   *
   * Defaults to `true` if not specified.
   */
  closeOnBackgroundClick?: boolean;
  /**
   * Determines whether the dialog window should be closed when the user presses
   * the Escape button.
   *
   * Defaults to `true` if not specified.
   */
  closeOnEscape?: boolean;
};

type Dialog = {
  component: DialogComponent;
  options: DialogOptions2;
};

type DialogContext = {
  /**
   * The currently shown dialog, if any.
   */
  shownDialog: Dialog | null;
  /**
   * All the open dialogs (including the shown and background dialogs).
   */
  dialogs: Dialog[];
  /**
   * Queues up the dialog window to be shown.
   */
  enqueue(component: DialogComponent, options: DialogOptions2): Dialog;
  /**
   * Dequeues the dialog window from being shown. Closes the window if it's already being shown.
   */
  dequeue(dialog: Dialog): void;
  /**
   * Updates the component of a dialog.
   */
  update(
    dialog: Dialog,
    component: DialogComponent,
    options: DialogOptions2,
  ): void;
};

const DialogsContext = createContext<DialogContext | null>(null);

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const dialogRoot = document.getElementById('dialog-root')!;

const popUp = keyframes`
  from {
    transform: translate(-50%, calc(-50% + 10px));
  }
  to {
    transform: translate(-50%, -50%);
  }
`;

const FADE_DURATION_MS = 100;

const Container = styled(Box).attrs({
  direction: 'horizontal',
  alignX: 'center',
})<{ show: boolean }>`
  position: absolute;
  width: auto;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${Styling.zIndex('dialog')};
  opacity: ${({ show }) => (show ? 1 : 0)};
  animation: ${popUp} ${FADE_DURATION_MS}ms linear 1;
  transition: opacity ${FADE_DURATION_MS}ms;
`;

export const DialogProvider2: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [dialogs, setDialogs] = useState<Dialog[]>([]);
  const shownDialog = useMemo(() => {
    if (dialogs.length === 0) {
      return null;
    }

    return dialogs[dialogs.length - 1];
  }, [dialogs]);
  const [isOpen, setIsOpen] = useState(false);
  const context = useMemo<DialogContext>(
    () => ({
      shownDialog,
      dialogs,
      enqueue(component, options) {
        const dialog = { component, options };

        setDialogs((dialogs) => [...dialogs, dialog]);

        return dialog;
      },
      dequeue(dialog) {
        const doClose = (): void => {
          setDialogs((dialogs) => {
            const copy = dialogs.slice();
            const index = copy.indexOf(dialog);

            if (index > -1) {
              copy.splice(index, 1);
              return copy;
            }

            return dialogs;
          });
        };

        if (dialogs.length <= 1) {
          doClose();
        } else {
          setIsOpen(false);
          setTimeout(doClose, FADE_DURATION_MS + 34);
        }
      },
      update(dialog, component, options) {
        setDialogs((dialogs) => {
          const copy = dialogs.slice();
          const index = copy.indexOf(dialog);

          if (index > -1) {
            // This is quite the hack. We update the array to trigger a re-render, and we mutate the object rather than
            // create a new one to maintain referential identity.
            copy[index].component = component;
            copy[index].options = options;
            return copy;
          }

          return dialogs;
        });
      },
    }),
    [shownDialog, dialogs],
  );

  useEffect(() => {
    setIsOpen(dialogs.length > 0);
  }, [dialogs]);

  useEffect(() => {
    const handleKeyDown = (evt: KeyboardEvent) => {
      if (
        evt.key === 'Escape' &&
        shownDialog &&
        shownDialog.options.closeOnEscape !== false
      ) {
        context.dequeue(shownDialog);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shownDialog]);

  const handleBackdropClick = () => {
    if (shownDialog && shownDialog.options.closeOnBackgroundClick !== false) {
      context.dequeue(shownDialog);
    }
  };

  return (
    <DialogsContext.Provider value={context}>
      {children}
      {ReactDOM.createPortal(
        <Backdrop
          show={dialogs.length > 0}
          durationMs={FADE_DURATION_MS}
          onClick={handleBackdropClick}
        >
          <Container show={isOpen}>
            {shownDialog && (
              <shownDialog.component
                onClose={() => context.dequeue(shownDialog)}
              />
            )}
          </Container>
        </Backdrop>,
        dialogRoot,
      )}
    </DialogsContext.Provider>
  );
};

export type UseDialog2<StaticProps> = {
  isOpen: boolean;
  isShown: boolean;
  open(props: StaticProps): void;
  close(): void;
};

export function useDialog2<StaticProps = {}>(
  Component: FC<StaticProps & DialogProps2>,
  options: DialogOptions2,
  deps: DependencyList,
): UseDialog2<StaticProps> {
  const context = useContext(DialogsContext);
  const dialog = useRef<Dialog>();
  const staticProps = useRef<StaticProps>();

  if (!context) {
    throw new Error('useDialog hook must be used within a DialogsProvider');
  }

  const isShown = dialog.current
    ? context.shownDialog === dialog.current
    : false;
  const isOpen = dialog.current
    ? context.dialogs.includes(dialog.current)
    : false;

  useEffect(() => {
    if (!isOpen) {
      dialog.current = undefined;
      staticProps.current = undefined;
    }
  }, [isOpen]);

  useEffect(() => {
    if (dialog.current && staticProps.current) {
      context.update(
        dialog.current,
        (dialogProps) => (
          <Component {...staticProps.current!} {...dialogProps} />
        ),
        options,
      );
    }
  }, deps);

  const open = (props: StaticProps) => {
    if (!dialog.current) {
      dialog.current = context.enqueue(
        (dialogProps) => <Component {...props} {...dialogProps} />,
        options,
      );
      staticProps.current = props;
    }
  };

  const close = () => {
    if (dialog.current) {
      context.dequeue(dialog.current);
    }
  };

  return { isOpen, isShown, open, close };
}

export type UseDialogs = {
  open(
    dialog: FC<DialogProps2>,
    options?: DialogOptions2,
  ): {
    close(): void;
  };
};

export function useDialogs(): UseDialogs {
  const context = useContext(DialogsContext);

  if (!context) {
    throw new Error('useDialogs hook must be used within a DialogsProvider');
  }

  return useMemo(
    () => ({
      open(dialog, options) {
        const handle = context.enqueue(dialog, {
          closeOnBackgroundClick: options?.closeOnBackgroundClick ?? true,
          closeOnEscape: options?.closeOnEscape ?? true,
        });

        return {
          close() {
            context.dequeue(handle);
          },
        };
      },
    }),
    [],
  );
}
